'use client'

import { createContext, PropsWithChildren, ReactNode, useContext, useMemo } from 'react'

type SellerContextT = {
  seller: Nullable<Transformed.Seller>
  sitePath: Nullable<string>
  siteCards: SiteCardT[]
}

type SellerProviderProps = {
  children: ReactNode
  seller: Nullable<Transformed.Seller>
  sitePath: string
  /** Every site card (getSiteCardsAction); none when omitted or when the sellers couldn't be fetched */
  siteCards?: SiteCardT[]
}

/** One shared empty list, so an omitted prop doesn't change the context value each render */
const NO_SITE_CARDS: SiteCardT[] = []

const SellerContext = createContext<SellerContextT>({
  seller: null,
  sitePath: null,
  siteCards: NO_SITE_CARDS,
})

export const SellerProvider = ({
  children,
  seller,
  sitePath,
  siteCards = NO_SITE_CARDS,
}: PropsWithChildren<SellerProviderProps>): ReactNode => {
  const currentSeller = useMemo<Nullable<Transformed.Seller>>(() => seller, [seller])
  const currentSitePath = useMemo<Nullable<string>>(() => sitePath, [sitePath])

  const value = useMemo(() => ({
    seller: currentSeller,
    sitePath: currentSitePath,
    siteCards,
  }), [currentSeller, currentSitePath, siteCards])

  return (
    <SellerContext value={value}>
      {children}
    </SellerContext>
  )
}

export const useSeller = (): Transformed.Seller => {
  const sellerContext = useContext(SellerContext)

  if (!sellerContext.seller) {
    throw new Error('Seller is not defined')
  }

  return sellerContext.seller
}

export const useSitePath = (): string => {
  const sellerContext = useContext(SellerContext)

  if (!sellerContext.sitePath) {
    throw new Error('Site path is not defined')
  }

  return sellerContext.sitePath
}

export const useOptionalSitePath = (): Nullable<string> => useContext(SellerContext).sitePath

/** Every site card, for cross-seller UI like the location pills */
export const useSiteCards = (): SiteCardT[] => useContext(SellerContext).siteCards
